import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

const IGNORE = '';

// ── Auto-suggest heuristics ───────────────────────────────────────────────────
// Called after columns are detected from a sample file. Returns a best-guess
// assignment dict {customerCol: internalName} based on common naming patterns.
// Handles: Rand Refinery (CE-01(V)), BioScope (CPL_001, CPO_001),
//          Conticon (CE-01-PV, TT-01-PV, REC_V-01-PV), and similar formats.

function autoAssign(cols, targets) {
  const allInternal = Object.values(targets).flat();
  const result = {};
  const used = new Set();

  const tryAssign = (col, target) => {
    if (target && allInternal.includes(target) && !used.has(target)) {
      result[col] = target;
      used.add(target);
      return true;
    }
    return false;
  };

  // Pick first unused from a list
  const nextUnused = (slots) => slots.find(s => !used.has(s));

  // Try exact internal name first, then fall back to nextUnused
  const tryExactOrNext = (col, exact, fallbackSlots) => {
    if (!tryAssign(col, exact)) tryAssign(col, nextUnused(fallbackSlots));
  };

  for (const col of cols) {
    // Normalise: uppercase, strip common suffixes (-PV, (V), (C), (%), (m3/hr))
    const c = col.toUpperCase()
      .replace(/-PV$/,      '')   // Conticon suffix
      .replace(/\([^)]*\)$/, '') // unit suffixes like (V), (C), (%)
      .replace(/\s+/g, '_')
      .trim();

    // ── Cell voltages ──────────────────────────────────────────────────────

    // "Cell Voltage-N" or "Cell Voltage N" (Sircel format)
    let m = c.match(/^CELL\s*VOLTAGE[-_\s]?0*(\d+)$/);
    if (m) {
      tryAssign(col, `CE-${String(parseInt(m[1])).padStart(2, '0')}`); continue;
    }

    // CPL_001..CPL_015 → CE-01..CE-15  (BioScope load-side)
    m = c.match(/^CPL[_-]?0*(\d+)$/);
    if (m) {
      tryAssign(col, `CE-${String(parseInt(m[1])).padStart(2, '0')}`); continue;
    }

    // CPO_001..CPO_015 → CE-16..CE-30  (BioScope open-circuit side)
    m = c.match(/^CPO[_-]?0*(\d+)$/);
    if (m) {
      tryAssign(col, `CE-${String(parseInt(m[1]) + 15).padStart(2, '0')}`); continue;
    }

    // CE-XX or CE-XX(V) — standard & Conticon (after suffix strip)
    m = c.match(/^CE-?0*(\d+)$/);
    if (m) {
      tryAssign(col, `CE-${String(parseInt(m[1])).padStart(2, '0')}`); continue;
    }

    // ── Rectifier ─────────────────────────────────────────────────────────
    // Match voltage vs current by presence of _V / _A / -V / -A / VI / AI
    if (/^REC/.test(c)) {
      const isVoltage = /_V$|-V$|VI$|_VI$/.test(c) || /REC_V/.test(c);
      const isCurrent = /_A$|-A$|AI$|_AI$/.test(c) || /REC_A/.test(c);
      if (isVoltage) {
        tryAssign(col, nextUnused(['REC-VI', 'REC-02-V', 'REC-03-V', 'REC-04-V']));
      } else if (isCurrent) {
        tryAssign(col, nextUnused(['REC-AI', 'REC-02-A', 'REC-03-A', 'REC-04-A']));
      } else {
        // Unknown REC column — grab next available slot
        tryAssign(col, nextUnused([
          'REC-AI', 'REC-VI', 'REC-02-A', 'REC-02-V', 'REC-03-A', 'REC-03-V', 'REC-04-A', 'REC-04-V',
        ]));
      }
      continue;
    }

    // ── Temperature ───────────────────────────────────────────────────────
    m = c.match(/^TT-?0*(\d+)$/);
    if (m) {
      const n = parseInt(m[1]);
      const exact = `TT-${String(n).padStart(2, '0')}`;
      tryExactOrNext(col, exact, ['TT-01','TT-02','TT-03','TT-04','TT-05','TT-06','TT-07','TT-08']);
      continue;
    }
    if (/^TT/.test(c)) {
      tryAssign(col, nextUnused(['TT-01','TT-02','TT-03','TT-04','TT-05','TT-06','TT-07','TT-08']));
      continue;
    }

    // ── Level ─────────────────────────────────────────────────────────────
    m = c.match(/^LT-?0*(\d+)$/);
    if (m) {
      const n = parseInt(m[1]);
      const exact = `LT-${String(n).padStart(2, '0')}`;
      tryExactOrNext(col, exact, ['LT-01','LT-02','LT-03','LT-04','LT-05','LT-06']);
      continue;
    }
    if (/^LT/.test(c)) {
      tryAssign(col, nextUnused(['LT-01','LT-02','LT-03','LT-04','LT-05','LT-06']));
      continue;
    }

    // ── Flow ──────────────────────────────────────────────────────────────
    m = c.match(/^F[IT]T?-?0*(\d+)$/);   // FT-XX or FIT-XX (Sircel)
    if (m) {
      const exact = `FT-${String(parseInt(m[1])).padStart(2, '0')}`;
      tryExactOrNext(col, exact, ['FT-01','FT-02']);
      continue;
    }
    if (/^F[IT]T?/.test(c)) {
      tryAssign(col, nextUnused(['FT-01','FT-02']));
      continue;
    }

    // ── Pressure ──────────────────────────────────────────────────────────
    m = c.match(/^DPT-?0*(\d+)$/);
    if (m) {
      const exact = `DPT-${String(parseInt(m[1])).padStart(2, '0')}`;
      tryExactOrNext(col, exact, ['DPT-01','DPT-02','DPT']);
      continue;
    }
    if (/^DPT$/.test(c)) { tryAssign(col, 'DPT'); continue; }

    m = c.match(/^PT-?0*(\d+)$/);
    if (m) {
      const exact = `PT-${String(parseInt(m[1])).padStart(2, '0')}`;
      const ptSlots = Array.from({length: 14}, (_, i) => `PT-${String(i+1).padStart(2,'0')}`);
      tryExactOrNext(col, exact, ptSlots);
      continue;
    }

    // ── pH ────────────────────────────────────────────────────────────────
    m = c.match(/^PH-?0*(\d+)$/);
    if (m) {
      const exact = `PH-${String(parseInt(m[1])).padStart(2, '0')}`;
      tryExactOrNext(col, exact, ['PH-01','PH-02','PH-03','PH-04']);
      continue;
    }
    if (/^PH$/.test(c)) { tryAssign(col, nextUnused(['PH-01','PH-02','PH-03','PH-04'])); continue; }

    // ── Pump / agitator speed ─────────────────────────────────────────────
    const pumpSlots = ['P-01-SPEED','P-02-SPEED','P-03-SPEED','P-04-SPEED'];
    if (/SPEED/.test(c) || /^(PP[LO]|AG\d|P[13]_SPEED)/.test(c)) {
      tryAssign(col, nextUnused(pumpSlots)); continue;
    }
    m = c.match(/^P-?0*(\d+)-?SPEED/);
    if (m) {
      const exact = `P-${String(parseInt(m[1])).padStart(2, '0')}-SPEED`;
      tryExactOrNext(col, exact, pumpSlots);
      continue;
    }
  }

  return result;
}

export default function ColumnMapper({ projectId, projectName }) {
  const [loading, setLoading]       = useState(true);
  const [targets, setTargets]       = useState({});       // {groupLabel: [internalName]}
  const [defaultMapping, setDefaultMapping] = useState(null);
  const [customerCols, setCustomerCols] = useState([]);   // column names from the file
  const [timestampCol, setTimestampCol] = useState('');
  const [assignments, setAssignments]   = useState({});   // {custCol: internalName | ''}
  const [headerRow, setHeaderRow]   = useState(0);        // which Excel/CSV row has headers
  const [dayfirst, setDayfirst]      = useState(false);
  const [detecting, setDetecting]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const fileRef = useRef();

  // Load existing mapping on mount
  useEffect(() => {
    setLoading(true);
    api.get(`/api/projects/${projectId}/mapping`)
      .then(res => {
        setTargets(res.data.internal_targets || {});
        setDefaultMapping(res.data.default_mapping || null);
        const m = res.data.mapping;
        if (m) {
          setTimestampCol(m.timestamp_col || '');
          setHeaderRow(m.header_row ?? 0);
          setDayfirst(m.dayfirst ?? false);
          const cols = Object.keys(m.columns || {});
          setCustomerCols(cols);
          setAssignments(m.columns || {});
        }
      })
      .catch(() => setError('Failed to load mapping'))
      .finally(() => setLoading(false));
  }, [projectId]);

  // Upload a sample file to detect its columns
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setDetecting(true); setError(''); setSuccess('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/api/projects/${projectId}/preview-columns`, fd);
      const cols = res.data.columns;
      const detectedHeaderRow = res.data.header_row ?? 0;
      // Auto-detect timestamp column
      const tsGuess = cols.find(c => /date|time|timestamp/i.test(c)) || '';
      setCustomerCols(cols);
      setHeaderRow(detectedHeaderRow);
      if (!timestampCol) setTimestampCol(tsGuess);
      // Auto-suggest assignments, then overlay any existing saved assignments
      setAssignments(prev => {
        const suggested = autoAssign(
          cols.filter(c => c !== tsGuess),
          targets,
        );
        // Existing (saved) assignments take priority over suggestions
        const next = { ...suggested };
        cols.forEach(c => { if (prev[c] !== undefined) next[c] = prev[c]; });
        return next;
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not read file columns');
    } finally {
      setDetecting(false);
    }
  };

  // Load the default (emew standard) mapping into the UI
  const loadDefault = () => {
    if (!defaultMapping) return;
    const cols = Object.keys(defaultMapping.columns);
    setTimestampCol(defaultMapping.timestamp_col);
    setCustomerCols(cols);
    setAssignments(defaultMapping.columns);
    setSuccess('Default mapping loaded — review and save when ready');
  };

  const setAssignment = (col, value) => {
    setAssignments(prev => ({ ...prev, [col]: value }));
  };

  const saveMapping = async () => {
    if (!timestampCol) { setError('Please select a timestamp column'); return; }
    if (!customerCols.length) { setError('No columns to map — upload a sample file first'); return; }
    setSaving(true); setError(''); setSuccess('');
    // Only include non-ignored assignments
    const columns = {};
    customerCols.forEach(c => {
      if (c !== timestampCol && assignments[c]) {
        columns[c] = assignments[c];
      }
    });
    try {
      await api.put(`/api/projects/${projectId}/mapping`, {
        timestamp_col: timestampCol,
        columns,
        header_row: headerRow,
        dayfirst,
      });
      setSuccess('Column mapping saved — new uploads will use this mapping');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  // Flat list of all internal target names (for duplicate detection)
  const allTargetNames = Object.values(targets).flat();

  // Which internal names are already used (to highlight conflicts)
  const usedTargets = Object.values(assignments).filter(Boolean);
  const duplicates = usedTargets.filter((v, i) => usedTargets.indexOf(v) !== i);

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>;
  }

  return (
    <div className="column-mapper">
      <div className="mapper-toolbar">
        <div style={{ flex: 1 }}>
          <div className="mapper-title">Column Mapping — <strong>{projectName}</strong></div>
          <div className="mapper-subtitle">
            Map this customer's column headers to emew's internal names.
            Upload a sample file to auto-detect columns.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button
            className="btn btn-ghost btn-sm"
            disabled={detecting}
            onClick={() => fileRef.current?.click()}
          >
            {detecting ? 'Reading…' : '📂 Upload Sample File'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={loadDefault}>
            ↺ Load Default
          </button>
          <button
            className="btn btn-primary btn-sm"
            disabled={saving || !customerCols.length}
            onClick={saveMapping}
          >
            {saving ? 'Saving…' : 'Save Mapping'}
          </button>
        </div>
      </div>

      {error   && <div className="alert alert-error"   style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 12 }}>{success}</div>}

      {customerCols.length === 0 ? (
        <div className="mapper-empty">
          <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
          <div>Upload a sample file from this customer to see its columns,</div>
          <div>or click <strong>↺ Load Default</strong> to use the emew standard format.</div>
        </div>
      ) : (
        <>
          {/* Header row selector */}
          <div className="mapper-ts-row">
            <span className="mapper-ts-label">Header on row</span>
            <select
              value={headerRow}
              onChange={e => setHeaderRow(parseInt(e.target.value))}
              className="mapper-ts-select"
              style={{ minWidth: 100 }}
            >
              <option value={0}>Row 1 (default)</option>
              <option value={1}>Row 2</option>
              <option value={2}>Row 3</option>
            </select>
            <span className="mapper-ts-hint">
              Change if column names appear in a different row — re-upload sample to re-detect
            </span>
          </div>

          {/* Timestamp row */}
          <div className="mapper-ts-row">
            <span className="mapper-ts-label">Timestamp column</span>
            <select
              value={timestampCol}
              onChange={e => setTimestampCol(e.target.value)}
              className="mapper-ts-select"
            >
              <option value="">(select…)</option>
              {customerCols.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <span className="mapper-ts-hint">Required — identifies the date/time of each reading</span>
          </div>

          {/* Date format row */}
          <div className="mapper-ts-row">
            <span className="mapper-ts-label">Date format</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={dayfirst}
                onChange={e => setDayfirst(e.target.checked)}
              />
              Day before month (DD/MM/YYYY)
            </label>
            <span className="mapper-ts-hint">
              Enable when dates look like <em>7/5/2026</em> meaning May 7 — disable for US format <em>5/7/2026</em> (July 5) or ISO <em>2026-05-07</em>
            </span>
          </div>

          {/* Column assignment table */}
          <table className="mapper-table">
            <thead>
              <tr>
                <th>Customer Column</th>
                <th>Maps To</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {customerCols
                .filter(c => c !== timestampCol)
                .map(col => {
                  const val = assignments[col] || IGNORE;
                  const isDuplicate = val && duplicates.includes(val);
                  return (
                    <tr key={col} className={isDuplicate ? 'mapper-row-warn' : ''}>
                      <td className="mapper-col-name">{col}</td>
                      <td>
                        <select
                          value={val}
                          onChange={e => setAssignment(col, e.target.value)}
                          className="mapper-select"
                        >
                          <option value="">(ignore)</option>
                          {Object.entries(targets).map(([group, names]) => (
                            <optgroup key={group} label={group}>
                              {names.map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                      <td className="mapper-status">
                        {isDuplicate && (
                          <span className="mapper-warn">⚠ duplicate</span>
                        )}
                        {!val && (
                          <span className="mapper-ignored">ignored</span>
                        )}
                        {val && !isDuplicate && (
                          <span className="mapper-ok">✓</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          <div className="mapper-footer">
            {customerCols.filter(c => c !== timestampCol && assignments[c]).length} of{' '}
            {customerCols.filter(c => c !== timestampCol).length} columns mapped
          </div>
        </>
      )}
    </div>
  );
}
